import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Volume2, Square, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One shared player so narrations never overlap.
let _player = null;
let _activeStop = null;

function getPlayer() {
  if (!_player) _player = new Audio();
  return _player;
}

// Reusable "listen" button: text → /api/tts/speak → play returned mp3 URL.
export default function ListenButton({ text, size = 30, testId = "listen-btn", label = null }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (_activeStop === stop) stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = () => {
    const p = getPlayer();
    p.pause();
    p.currentTime = 0;
    _activeStop = null;
    if (mountedRef.current) setState("idle");
  };

  const play = async () => {
    if (state === "playing") {
      stop();
      return;
    }
    if (_activeStop) _activeStop();
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/tts/speak`, { text: (text || "").slice(0, 2600) });
      if (!mountedRef.current) return;
      const p = getPlayer();
      p.src = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      p.onended = () => {
        _activeStop = null;
        if (mountedRef.current) setState("idle");
      };
      p.onerror = () => {
        _activeStop = null;
        if (mountedRef.current) setState("idle");
      };
      await p.play();
      _activeStop = stop;
      if (mountedRef.current) setState("playing");
    } catch (err) {
      console.error("TTS playback failed", err);
      if (mountedRef.current) setState("idle");
    }
  };

  const disabled = !text || !text.trim();

  return (
    <button
      type="button"
      onClick={play}
      disabled={disabled || state === "loading"}
      className="inline-flex items-center justify-center gap-1.5 transition-all flex-shrink-0"
      style={{
        minWidth: size,
        height: size,
        padding: label ? "0 10px" : 0,
        borderRadius: "2px",
        border: `1px solid ${state === "playing" ? "var(--iw-accent-red)" : "var(--iw-border)"}`,
        background: "#fff",
        color: state === "playing" ? "var(--iw-accent-red)" : "var(--iw-primary)",
        cursor: disabled || state === "loading" ? "not-allowed" : "pointer",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.1em",
      }}
      aria-label={state === "playing" ? "Stop audio" : "Listen"}
      title={state === "playing" ? "Stop" : "Listen"}
      data-testid={testId}
    >
      {state === "loading" ? (
        <Loader2 size={size * 0.5} className="animate-spin" aria-hidden="true" />
      ) : state === "playing" ? (
        <Square size={size * 0.42} fill="currentColor" aria-hidden="true" />
      ) : (
        <Volume2 size={size * 0.5} aria-hidden="true" />
      )}
      {label && <span className="iw-mono uppercase">{state === "playing" ? "Stop" : label}</span>}
    </button>
  );
}
